import m from 'mithril';

type CacheEntry<T> = {
  data: T;
  expiresAt: number;
  loading?: boolean;
  error?: Error;
};

type FetcherFunction<T> = () => Promise<T>;

export interface CacheOptions {
  ttl?: number;
  autoRedraw?: boolean;
  key?: string;
}

export interface QueryOptions extends CacheOptions {
  retry?: boolean; // Allow retry on error
}

export interface QueryResponse<T> {
	data: T | undefined
	error: Error | undefined
	loading: boolean
	retry?: () => void
}

export class FetchCache {
  public cache = new Map<string, CacheEntry<unknown>>();
  private inflight = new Map<string, Promise<unknown>>();
  private defaultTTL: number;
  private autoRedraw: boolean;

  constructor(defaultTTL = 60 * 60 * 1000, autoRedraw = true) { // 1 hour default
    this.defaultTTL = defaultTTL;
    this.autoRedraw = autoRedraw;
  }

  /**
   * Get data from cache or fetch if not available/expired
   * @param key - Unique cache key
   * @param fetcher - Function that returns a Promise with the data
   * @param options - Cache options (ttl, autoRedraw)
   */
  async get<T>(
    key: string,
    fetcher: FetcherFunction<T>,
    options: CacheOptions = {}
  ): Promise<T> {
    const now = Date.now();
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    const ttl = options.ttl ?? this.defaultTTL;
    const shouldRedraw = options.autoRedraw ?? this.autoRedraw;

    // Return cached data if valid
    if (entry && entry.expiresAt > now && !entry.loading) {
      return entry.data;
    }

    // Return in-flight promise if already fetching
    if (this.inflight.has(key)) {
      return this.inflight.get(key) as Promise<T>;
    }

    // Set loading state
    this.cache.set(key, {
      data: entry?.data as T,
      expiresAt: entry?.expiresAt ?? 0,
      loading: true,
      error: undefined, // Clear any previous error
    });

    if (shouldRedraw) {
      m.redraw();
    }

    // Create fetch promise
    const fetchPromise = fetcher()
      .then((data) => {
        this.cache.set(key, {
          data,
          expiresAt: now + ttl,
          loading: false,
          error: undefined, // Clear any previous error
        });
        this.inflight.delete(key);

        if (shouldRedraw) {
          m.redraw();
        }

        return data;
      })
      .catch((err) => {
        // Store error state - don't expire the entry so error persists
        const errorEntry = {
          data: entry?.data as T,
          expiresAt: Date.now() + (options.ttl ?? this.defaultTTL), // Keep error state cached
          loading: false,
          error: err,
        };

        this.cache.set(key, errorEntry);
        this.inflight.delete(key);

        if (shouldRedraw) {
          m.redraw();
        }

        throw err;
      });

    this.inflight.set(key, fetchPromise);
    return fetchPromise;
  }

  /**
   * Check if data is currently loading
   */
  isLoading(key: string): boolean {
    const entry = this.cache.get(key) as CacheEntry<unknown> | undefined;
    return entry?.loading ?? false;
  }

  /**
   * Get error state for a cache key
   */
  getError(key: string): Error | undefined {
    const entry = this.cache.get(key) as CacheEntry<unknown> | undefined;
    return entry?.error;
  }

  /**
   * Get cached data without triggering a fetch
   * Returns data if valid OR if there's an error state (to show error)
   */
  peek<T>(key: string): T | undefined {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) return undefined;

    // Return data if still valid OR if there's an error (error states should persist)
    if (entry.expiresAt > Date.now() || entry.error) {
      return entry.data;
    }

    return undefined;
  }

  /**
   * Get cached data even if expired (useful for showing stale data while loading)
   */
  peekStale<T>(key: string): T | undefined {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    return entry?.data;
  }

  /**
   * Clear cache entry or entire cache
   */
  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
      this.inflight.delete(key);
    } else {
      this.cache.clear();
      this.inflight.clear();
    }
  }

  /**
   * Invalidate cache entry (mark as expired)
   */
  invalidate(key: string): void {
    const entry = this.cache.get(key);
    if (entry) {
      this.cache.set(key, {
        ...entry,
        expiresAt: 0,
        error: undefined, // Clear error when invalidating
      });
    }
  }

  /**
   * Clear error state for a cache key
   */
  clearError(key: string): void {
    const entry = this.cache.get(key);
    if (entry) {
      this.cache.set(key, {
        ...entry,
        error: undefined,
      });
    }
  }

  /**
   * Preload data into cache
   */
  async preload<T>(key: string, fetcher: FetcherFunction<T>, options: CacheOptions = {}): Promise<void> {
    try {
      await this.get(key, fetcher, { ...options, autoRedraw: false });
    } catch (err) {
      // Ignore preload errors
      console.warn(`Preload failed for key: ${key}`, err);
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    let loadingEntries = 0;

    for (const entry of this.cache.values()) {
      const typedEntry = entry as CacheEntry<unknown>;
      if (typedEntry.loading) {
        loadingEntries++;
      } else if (typedEntry.expiresAt > now) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      loadingEntries,
      inflightRequests: this.inflight.size,
    };
  }
}

// Global instance
export const fetchCache = new FetchCache();

/**
 * TanStack Query-style API that returns { data, error, loading }
 *
 * This provides a more convenient API for components that want to handle
 * data, error, and loading states in a structured way.
 *
 * @param key - Unique cache key
 * @param fetcher - Function that returns a Promise with the data
 * @param options - Cache options (ttl, autoRedraw)
 * @returns Object with data, error, and loading properties
 */
export function query<T>(
  key: string,
  fetcher: FetcherFunction<T>,
  options: QueryOptions = {}
): QueryResponse<T> {
  const loading = fetchCache.isLoading(key);
  const error = fetchCache.getError(key);
  const cachedData = fetchCache.peek<T>(key);

  // Determine if we should trigger a fetch
  const shouldFetch = !loading && !error && !cachedData;

  if (shouldFetch) {
    fetchCache.get(key, fetcher, options)
      .catch((err) => {
        // Error will be stored in cache and available on next query() call
        // This is just to prevent unhandled promise rejection
      });
  }

  // Use cached data, or stale data if loading
  let data: T | undefined = cachedData;
  if (loading && !data) {
    data = fetchCache.peekStale<T>(key);
  }

  // Provide retry function for error states
  const retry = error ? () => {
    fetchCache.clearError(key);
    fetchCache.invalidate(key);
    // Trigger immediate refetch after clearing error
    fetchCache.get(key, fetcher, options).catch(() => {});
  } : undefined;

  return { data, error, loading, retry };
}

/**
 * True while a resource is still loading OR hasn't been queried yet at all.
 * query() reports loading:false for one frame on a resource's first call, before its fetch is
 * registered as in-flight -- checking data/error instead of !loading catches that frame too,
 * while still resolving to false once a fetch completes, even with a legitimate null/empty result.
 */
export function isQueryResourcePending<T>(res: QueryResponse<T>): boolean {
  return res.loading || (res.data === undefined && res.error === undefined);
}
